import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton, LinkedinShareButton, FacebookIcon, TwitterIcon, WhatsappIcon, LinkedinIcon } from 'react-share';
import { getBlogBySlug } from '../services/blogService';
import { trackBlogView } from '../services/analytics';
import LikeButton from '../components/blog/LikeButton';
import CommentSection from '../components/blog/CommentSection';
// Analytics disabled
import '../components/blog/blogStyles.css';

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const blogData = await getBlogBySlug(slug);
        if (blogData) {
          setBlog(blogData);
          // Track blog view
          trackBlogView(slug, blogData.title);
        } else {
          setError('Blog not found');
        }
      } catch (err) {
        setError('Failed to load blog');
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug]);


  const calculateReadingTime = (content) => {
    const wordsPerMinute = 200;
    const words = content.split(' ').length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return minutes;
  };



  if (loading) return (
    <div className="blog-detail-loading">
      <div className="container">
        <div className="loading-skeleton">
          <div className="skeleton-hero"></div>
          <div className="skeleton-content">
            <div className="skeleton-title"></div>
            <div className="skeleton-text"></div>
            <div className="skeleton-text"></div>
          </div>
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div className="blog-detail-error">
      <div className="container">
        <div className="error-content">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 9V11M12 15H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <h2>{error}</h2>
          <p>The article you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    </div>
  );

  if (!blog) return null;

  const shareUrl = window.location.href;
  const title = blog.title;
  const readingTime = calculateReadingTime(blog.content);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": blog.title,
    "image": blog.thumbnailUrl,
    "datePublished": blog.createdAt.toDate().toISOString(),
    "author": {
      "@type": "Person",
      "name": blog.author
    },
    "description": blog.summary,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": blog.canonical || shareUrl
    }
  };

  return (
    <div className="blog-detail">
      <Helmet>
        <title>{blog.metaTitle || blog.title}</title>
        <meta name="description" content={blog.metaDescription || blog.excerpt} />
        <link rel="canonical" href={blog.canonical || shareUrl} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="blog-hero">
        <div className="blog-hero-image">
          {blog.thumbnailUrl && (
            <img src={blog.thumbnailUrl} alt={blog.title} />
          )}
        </div>
        <div className="blog-hero-content">
          <div className="blog-category-chip">
            {blog.category || 'General'}
          </div>
          <h1 className="blog-title">{blog.title}</h1>
          <div className="blog-meta-chips">
            <span className="meta-chip author-chip">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {blog.author}
            </span>
            <span className="meta-chip date-chip">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {new Date(blog.createdAt.toDate()).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
            <span className="meta-chip reading-chip">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {readingTime} min read
            </span>
          </div>
        </div>
      </section>

      <div className="blog-content-wrapper">
        <div className="container">
          {/* Back Button */}
          <div className="mb-4">
            <button
              className="btn btn-outline-secondary"
              onClick={() => navigate('/blog')}
              style={{ borderColor: '#5a6f56', color: '#5a6f56' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="me-2">
                <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to Blog List
            </button>
          </div>

          <div className="blog-content-layout">
            {/* Main Content */}
            <main className="blog-main-content">
              <div className="blog-content">
                <ReactMarkdown
                  components={{
                    h1: ({ children }) => <h1>{children}</h1>,
                    h2: ({ children }) => <h2>{children}</h2>,
                    h3: ({ children }) => <h3>{children}</h3>,
                    h4: ({ children }) => <h4>{children}</h4>,
                    h5: ({ children }) => <h5>{children}</h5>,
                    h6: ({ children }) => <h6>{children}</h6>,
                    blockquote: ({ children }) => (
                      <blockquote className="blog-blockquote">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 17H17L19 13V7H13V13H16M6 17H9L11 13V7H5V13H8L6 17Z" fill="currentColor"/>
                        </svg>
                        {children}
                      </blockquote>
                    ),
                    ul: ({ children }) => <ul className="blog-list">{children}</ul>,
                    ol: ({ children }) => <ol className="blog-list numbered">{children}</ol>,
                    p: ({ children }) => <p className="blog-paragraph">{children}</p>,
                    strong: ({ children }) => <strong className="blog-strong">{children}</strong>,
                    em: ({ children }) => <em className="blog-emphasis">{children}</em>,
                  }}
                >
                  {blog.content}
                </ReactMarkdown>
              </div>

              {/* Article Actions */}
              <div className="blog-actions">
                <div className="blog-tags">
                  {blog.tags?.map(tag => (
                    <span key={tag} className="blog-tag">{tag}</span>
                  ))}
                </div>

                <div className="blog-share-section">
                  <h4>Share this article</h4>
                  <div className="share-buttons">
                    <FacebookShareButton url={shareUrl} quote={title}>
                      <FacebookIcon size={40} round />
                    </FacebookShareButton>
                    <TwitterShareButton url={shareUrl} title={title}>
                      <TwitterIcon size={40} round />
                    </TwitterShareButton>
                    <WhatsappShareButton url={shareUrl} title={title}>
                      <WhatsappIcon size={40} round />
                    </WhatsappShareButton>
                    <LinkedinShareButton url={shareUrl} title={title}>
                      <LinkedinIcon size={40} round />
                    </LinkedinShareButton>
                  </div>
                </div>

                <div className="blog-engagement">
                  <LikeButton blogId={blog.id} />
                </div>
              </div>

              {/* Comments Section */}
              <CommentSection blogId={blog.id} />
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;